import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, LogOut } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Logo } from "@/components/Logo";
import { NotificationBell } from "@/components/NotificationBell";
import { useAuth, homeFor } from "@/context/AuthContext";

const NAV = {
  customer: [["/customer", "Book"], ["/guide", "Local Guide"], ["/customer/trips", "My Trips"], ["/receipts", "Receipts"], ["/account", "Account"], ["/ratings", "Ratings"], ["/customer/promos", "Promos"], ["/get-the-app", "Get the App"], ["/support", "Support"]],
  driver: [["/driver", "Console"], ["/driver/hub", "Driver Hub"], ["/driver/earnings", "Earnings"], ["/ratings", "Ratings"], ["/driver/schedule", "Schedule"], ["/driver/training", "Training"], ["/driver/busy-areas", "Busy areas"], ["/driver/account", "Account"], ["/driver/trips", "My Trips"], ["/driver/promos", "Promos"], ["/get-the-app", "Get the App"], ["/support", "Support"]],
  admin: [["/admin", "Dashboard"]],
  business: [["/business", "Deliveries"], ["/support", "Support"], ["/account", "Account"]],
};

const VISIBLE = 6;

const linkCls = ({ isActive }) => `whitespace-nowrap rounded-full px-3 py-2 text-xs font-medium transition-colors sm:px-2.5 sm:text-[13px] xl:px-3.5 xl:text-sm ${isActive ? "bg-amber-400/10 text-amber-200" : "text-zinc-400 hover:text-amber-100"}`;

const MoreMenu = ({ items }) => {
  const { pathname } = useLocation();
  const active = items.some(([to]) => pathname === to);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid="nav-more" className={`hidden items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-2 text-[13px] font-medium transition-colors sm:inline-flex xl:px-3.5 xl:text-sm ${active ? "bg-amber-400/10 text-amber-200" : "text-zinc-400 hover:text-amber-100"}`}>
        More <ChevronDown className="h-3.5 w-3.5" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="z-[1400] border-amber-500/20 bg-[#121216] text-zinc-200">
        {items.map(([to, label]) => (
          <DropdownMenuItem key={to} asChild><Link to={to} data-testid={`nav-more-${label.toLowerCase().replace(/ /g, "-")}`} className="cursor-pointer">{label}</Link></DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const AppHeader = () => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const items = NAV[user?.role] || [];
  const overflow = items.length > VISIBLE + 1 ? items.slice(VISIBLE) : [];
  return (
    <header data-testid="app-header" className="sticky top-0 z-40 border-b border-amber-500/10 bg-[#0a0a0c]/85 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2 sm:h-24 sm:flex-nowrap sm:px-8 sm:py-0">
        <Logo to={homeFor(user?.role)} compact />
        <nav className="order-last -mx-1 flex w-full items-center gap-1 overflow-x-auto pb-1 sm:order-none sm:mx-0 sm:ml-4 sm:w-auto sm:min-w-0 sm:flex-1 sm:overflow-visible sm:pb-0">
          {items.map(([to, label], i) => (
            <NavLink key={to} to={to} end data-testid={`nav-${label.toLowerCase().replace(" ", "-")}`}
              className={(s) => `${linkCls(s)} ${overflow.length && i >= VISIBLE ? "sm:hidden" : ""}`}>
              {label}
            </NavLink>
          ))}
          {overflow.length > 0 && <MoreMenu items={overflow} />}
        </nav>
        <div className="ml-auto flex items-center gap-1 sm:gap-3">
          <span className="hidden text-right xl:block">
            <span className="block text-sm text-zinc-200" data-testid="header-user-name">{user?.name}</span>
            <span className="lux-eyebrow !text-[0.6rem]">{user?.role}</span>
          </span>
          <NotificationBell />
          <button data-testid="logout-button" onClick={() => { logout(); nav("/"); }} className="rounded-full p-2.5 text-zinc-400 transition-colors hover:bg-white/5 hover:text-amber-200" aria-label="Log out">
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      </div>
    </header>
  );
};

export const PortalShell = ({ children }) => (
  <div className="min-h-screen bg-[#0a0a0c]">
    <AppHeader />
    <main className="mx-auto max-w-7xl px-4 py-6 sm:px-8 sm:py-10">{children}</main>
  </div>
);
